/**
 * components/Settings/AI/AISetupWizard.tsx
 * Multi-step wizard container for AI provider setup
 *
 * Manages wizard state (current step, selected provider, API key) and
 * renders the appropriate step component with progress indicators and
 * back/next/cancel navigation.
 *
 * State is local (no Context). Credential is saved to IndexedDB only
 * on successful completion via `aiCredentialService.saveCredential()`.
 *
 * Work Unit: WU01 - AI Setup Wizard
 * Task: T01 - Setup Wizard Container
 * Sprint: S02 - AI Setup & Settings UI
 * Project: P09 - AI Integration
 */

// 1. External dependencies
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ShieldAlert } from 'lucide-react';

// 2. Workspace packages
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Progress,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AIProviderType } from '@/types/shared/AI';
import { aiCredentialService } from '@/services/AICredentialService';

// 4. Relative imports
import { APIKeyEntryStep } from './APIKeyEntryStep';
import { ProviderSelectionStep } from './ProviderSelectionStep';
import { ValidationPrivacyStep } from './ValidationPrivacyStep';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Step labels displayed in the progress indicator */
const WIZARD_STEPS = [
  'Select Provider',
  'Enter API Key',
  'Validate & Finish',
] as const;

/** Total number of steps in the wizard */
const TOTAL_STEPS = WIZARD_STEPS.length;

// ─── Props ──────────────────────────────────────────────────────────────────

/**
 * Props for the AI Setup Wizard container
 *
 * @see AISetupWizard
 */
export interface AISetupWizardProps {
  /** Called when the wizard completes successfully (credential saved) */
  onComplete: () => void;

  /** Called when the user cancels or closes the wizard */
  onCancel: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * AI Setup Wizard
 *
 * Guided multi-step wizard for first-time AI provider setup.
 * Three steps: provider selection, API key entry, validation & privacy.
 *
 * - State is local (`useState`) — no global state or Context
 * - Credential is persisted to IndexedDB only on successful completion
 * - Encryption availability is checked proactively on mount
 *
 * @example
 * ```tsx
 * <AISetupWizard
 *   onComplete={() => setShowWizard(false)}
 *   onCancel={() => setShowWizard(false)}
 * />
 * ```
 */
export const AISetupWizard: React.FC<AISetupWizardProps> = ({
  onComplete,
  onCancel,
}) => {
  // ─── State ────────────────────────────────────────────────────────────

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedProvider, setSelectedProvider] =
    useState<AIProviderType | null>(null);
  const [apiKey, setApiKey] = useState('');

  // ─── Encryption Check ─────────────────────────────────────────────────
  // Synchronous, cached after first call
  const isEncryptionAvailable = aiCredentialService.isEncryptionAvailable();

  // ─── Keyboard Navigation ──────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // ─── Navigation Handlers ──────────────────────────────────────────────

  /** Navigate to previous step with appropriate state resets */
  const handleBack = useCallback((): void => {
    if (currentStep === 1) {
      // Going back from key entry: reset key
      setApiKey('');
    }
    // ValidationPrivacyStep manages its own state and resets on remount
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, [currentStep]);

  /** Navigate to next step */
  const handleNext = useCallback((): void => {
    setCurrentStep((prev) => Math.min(TOTAL_STEPS - 1, prev + 1));
  }, []);

  /** Save credential and complete the wizard */
  const handleComplete = useCallback(async (): Promise<void> => {
    if (!selectedProvider) return;
    await aiCredentialService.saveCredential(selectedProvider, apiKey, 'valid');
    onComplete();
  }, [selectedProvider, apiKey, onComplete]);

  // ─── Derived State ────────────────────────────────────────────────────

  /** Whether the Next button should be disabled */
  const isNextDisabled =
    currentStep === 0
      ? selectedProvider === null
      : apiKey.trim().length === 0;

  // ─── Encryption Unavailable Gate ──────────────────────────────────────

  if (!isEncryptionAvailable) {
    return (
      <Card className="w-full">
        <CardBody className="p-4 sm:p-6">
          <Alert
            color="warning"
            title="Secure Connection Required"
            description="AI features require a secure (HTTPS) connection to encrypt and store your API key safely. Please access this page over HTTPS to set up your AI provider."
            icon={<ShieldAlert className="size-5" />}
          />
          <div className="mt-4 flex justify-end">
            <Button variant="light" onPress={onCancel}>
              Close
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // ─── Main Render ──────────────────────────────────────────────────────

  return (
    <Card className="w-full" aria-label="AI Provider Setup Wizard">
      {/* Step Indicator */}
      <CardHeader className="flex-col items-start gap-3 p-4 pb-0 sm:p-6 sm:pb-0">
        {/* Step labels */}
        <div className="flex w-full justify-between gap-1">
          {WIZARD_STEPS.map((label, index) => {
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            const colorClass = isActive
              ? 'text-primary font-semibold'
              : isCompleted
                ? 'text-primary'
                : 'text-default-400';

            return (
              <div
                key={label}
                className={`flex-1 text-center text-xs sm:text-sm ${colorClass}`}
              >
                <span className="sm:hidden">{index + 1}</span>
                <span className="hidden sm:inline">
                  {index + 1}. {label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <Progress
          value={currentStep + 1}
          maxValue={TOTAL_STEPS}
          size="sm"
          color="primary"
          className="w-full"
          aria-label={`Step ${currentStep + 1} of ${TOTAL_STEPS}: ${WIZARD_STEPS[currentStep]}`}
        />
      </CardHeader>

      {/* Step Content */}
      <CardBody className="p-4 sm:p-6">
        {currentStep === 0 && (
          <ProviderSelectionStep
            selectedProvider={selectedProvider}
            onSelect={setSelectedProvider}
          />
        )}
        {currentStep === 1 && selectedProvider && (
          <APIKeyEntryStep
            selectedProvider={selectedProvider}
            apiKey={apiKey}
            onApiKeyChange={setApiKey}
          />
        )}
        {currentStep === 2 && selectedProvider && (
          <ValidationPrivacyStep
            selectedProvider={selectedProvider}
            apiKey={apiKey}
            onComplete={handleComplete}
          />
        )}
      </CardBody>

      {/* Navigation Footer */}
      <CardFooter className="flex-col-reverse gap-2 p-4 pt-0 sm:flex-row sm:justify-between sm:p-6 sm:pt-0">
        <Button
          variant="light"
          onPress={onCancel}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>

        <div className="flex w-full gap-2 sm:w-auto">
          <Button
            variant="flat"
            isDisabled={currentStep === 0}
            onPress={handleBack}
            startContent={<ChevronLeft className="size-4" />}
            className="flex-1 sm:flex-initial"
          >
            Back
          </Button>

          {currentStep < TOTAL_STEPS - 1 && (
            <Button
              color="primary"
              isDisabled={isNextDisabled}
              onPress={handleNext}
              endContent={<ChevronRight className="size-4" />}
              className="flex-1 sm:flex-initial"
            >
              Next
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};
